import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { useNavigate } from 'react-router-dom';
import ChangePassword from './ChangePassword';

const BaseUrl = process.env.REACT_APP_BASE_URL;

export default function Dashboard() {
  const [data, setData] = useState ({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [showModal, setShowModal] = useState(false);
  const cookies = new Cookies();
  const token = cookies.get('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    axios.get(`${BaseUrl}vendor/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(res => setData(res.data.data));
  }, [token, msg]);

  // Handler for email verification
  const handleVerifyEmail = async () => {
    setLoading(true);
    setMsg('');
    try {
      await axios.post(
        `${BaseUrl}vendor/send_verify_email`,
        { email: data.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg('Verification email sent!');
    } catch {
      setMsg('Failed to send verification email.');
    }
    setLoading(false);
  };

  // Handler for phone verification
  const handleVerifyPhone = async () => {
    setLoading(true);
    setMsg('');
    try {
      await axios.post(
        `${BaseUrl}vendor/send_verify_phone`,
        {
          country_code: data.country_code,
          phone_number: data.phone_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg('Verification SMS sent!');
    } catch {
      setMsg('Failed to send verification SMS.');
    }
    setLoading(false);
  };

  // Handler for sign out
  const handleSignOut = async () => {
    setLoading(true);
    setMsg('');
    try {
      await axios.post(`${BaseUrl}vendor/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      // Even if API fails, clear token and redirect
    }
    cookies.remove('token');
    setLoading(false);
    navigate('/login', { replace: true }); // <-- Redirect immediately
  };

  return (
    <div className="min-vh-100 bg-light" style={{ fontSize: '.875rem' }}>
      {/* Navbar */}
      <nav className="navbar navbar-dark bg-dark sticky-top shadow">
        <div className="container-fluid">
          <span className="navbar-brand fw-bold" style={{
            backgroundColor: 'rgba(0,0,0,.25)',
            padding: '.5rem 1rem',
            borderRadius: '.25rem'
          }}>
            {data.company_name || '-'}
          </span>
          <button
            onClick={handleSignOut}
            className="btn btn-danger btn-sm fw-semibold"
            disabled={loading}
          >
            {loading ? "Signing out..." : "Sign Out"}
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container py-4">
        <div className="row">
          <main className="col-12">
            <div className="d-flex justify-content-between flex-wrap align-items-center pb-2 mb-3 border-bottom">
              <h1 className="h2 fw-bold">Dashboard</h1>
            </div>

            {msg && (
              <div className="alert alert-info text-center fw-semibold" role="alert">
                {msg}
              </div>
            )}

            <div className="card shadow-sm">
              <div className="card-body">
                <h2 className="h5 fw-semibold mb-4">Profile Data</h2>

                <div className="table-responsive">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th className="text-start" style={{ width: '30%' }}>Full Name</th>
                        <td>{data.full_name || '-'}</td>
                      </tr>
                      <tr>
                        <th className="text-start">Email</th>
                        <td>{data.email || '-'}</td>
                      </tr>
                      <tr>
                        <th className="text-start">Phone Number</th>
                        <td>{'+' + data.country_code + data.phone_number || '-'}</td>
                      </tr>
                      <tr>
                        <th className="text-start">Logo</th>
                        <td>
                          {data.logo ? (
                            <img src={data.logo} alt="Logo" className="rounded" style={{ height: '3rem' }} />
                          ) : '-'}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-start">Profile Image</th>
                        <td>
                          {data.profile_image ? (
                            <img src={data.profile_image} alt="Profile" className="rounded" style={{ height: '3rem' }} />
                          ) : '-'}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-start">Business Name</th>
                        <td>{data.business_name || '-'}</td>
                      </tr>
                      <tr>
                        <th className="text-start">Business Name (Ar)</th>
                        <td>{data.business_name_ar || '-'}</td>
                      </tr>
                      <tr>
                        <th className="text-start">Email Verified</th>
                        <td>
                          {data.is_email_verified == 1 ? (
                            <span className="text-success fw-semibold">Yes</span>
                          ) : (
                            <>
                              <span className="text-danger fw-semibold">No</span>
                              <button
                                onClick={handleVerifyEmail}
                                disabled={loading}
                                  className="btn btn-common btn-sm ms-3"
                              >
                                {loading ? "Sending..." : "Verify"}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-start">Phone Number Verified</th>
                        <td>
                          {data.is_phone_number_verified == 1 ? (
                            <span className="text-success fw-semibold">Yes</span>
                          ) : (
                            <>
                              <span className="text-danger fw-semibold">No</span>
                              <button
                                onClick={handleVerifyPhone}
                                disabled={loading}
                                  className="btn btn-common btn-sm ms-3"
                              >
                                {loading ? "Sending..." : "Verify"}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-start">Order Domain</th>
                        <td>
                          {data.orders_domain && data.orders_domain.startsWith('http') ? (
                            <a
                              href={data.orders_domain}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary text-decoration-underline"
                            >
                              {data.orders_domain}
                            </a>
                          ) : (
                            <span className="text-danger fw-semibold">{data.orders_domain}</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-start">Buffet Domain</th>
                        <td>
                          {data.buffet_domain && data.buffet_domain.startsWith('http') ? (
                            <a
                              href={data.buffet_domain}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary text-decoration-underline"
                            >
                              {data.buffet_domain}
                            </a>
                          ) : (
                            <span className="text-danger fw-semibold">{data.buffet_domain}</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={() => setShowModal(true)}
                  className="btn btn-common fw-semibold mt-3"
                >
                  Change Password
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <ChangePassword onClose={() => setShowModal(false)} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
